import { EExecutionMode, EFillPolicy, EMarginMode, EProfitMode } from '@aqx-core/common/api/order';

export enum EHideType {
  CATEGORY = 'CATEGORY',
  SYMBOL = 'SYMBOL'
}

export enum ESymbolCategory {
  FOREX = 'FOREX',
  SHARES = 'SHARE',
  COMMODITIES = 'COM',
  INDEX = 'INDEX',
  CRYPTO = 'CRYPTO',
  OTHERS = 'OTHER',
  FUTURES = 'FUTURES',
  OPTION = 'OPTION',
  EQUITIES = 'EQUITIES',
  ETFS = 'ETFS',
  ALL = 'ALL'
}

export enum EOptionSymbolSide {
  PUT = 'PUT',
  CALL = 'CALL'
}

export enum ESymbolStatus {
  TRADING = 'TRADING',
  CLOSED = 'CLOSED',
  OFFQUOTES = 'OFFQUOTES'
}

export enum ETradeMode {
  TRADE_NO,
  TRADE_CLOSE,
  TRADE_FULL,
  TRADE_LONG,
  TRADE_SHORT
}

export enum EGtcPendings {
  ORDERS_DAILY,
  ORDERS_GTC,
  ORDERS_DAILY_NO_STOPS
}

export enum ESwapType {
  SWAP_BY_POINTS,
  SWAP_BY_DOLLARS,
  SWAP_BY_INTEREST,
  SWAP_BY_MARGIN_CURRENCY,
  SWAP_DISABLED,
  SWAP_BY_SYMBOL_CURRENCY,
  SWAP_BY_GROUP_CURRENCY,
  SWAP_BY_INTEREST_CURRENT,
  SWAP_BY_INTEREST_OPEN,
  SWAP_REOPEN_BY_CLOSE_PRICE,
  SWAP_REOPEN_BY_BID,
  SWAP_BY_PROFIT_CURRENCY,
  /** Centroid */
  SWAP_FIXED_MONEY_PER_LOT,
  SWAP_BY_POINT_CENTROID,
  SWAP_BY_PRICE_PERCENTAGE
}

export enum ESwapRollover3Days {
  NONE = 0,
  MON = 1,
  TUE = 2,
  WED = 3,
  THUR = 4,
  FRI = 5,
  SAT = 6,
  SUN = 7
}

export enum ESymbolFillPolicy {
  NONE,
  FOK,
  IOC,
  ALL
}

export enum EUISymbolInstrumentType {
  OPTION = 'option',
  EQUITY = 'equity'
}

export enum EOptionExcersiceStyle {
  AMERICAN = 'AMERICAN',
  EUROPEAN = 'EUROPEAN',
  BERMUDAN = 'BERMUDAN'
}

export enum ESymbolPriceType {
  PERCENTAGE = 'PERCENTAGE',
  PER_UNIT = 'PER_UNIT'
}

// Market APIs params
export interface IHideMarketSymbolParams {
  type: EHideType;
  values: string[];
  showAll: boolean;
}

export interface IGetSymbolDetailParams {
  symbol: string;
}

export interface ISearchSymbolsParams {
  text: string;
}

export interface IDeleteSearchHistoryParams {
  symbol?: string;
}

// Market APIs response
export interface IMarketSymbolCategory {
  symbolTypeList: ESymbolCategory[];
}

export interface ISymbol {
  symbol: string; // Can be ticker or equity symbol as id
  symbolGroup: string;
  type: ESymbolCategory;
  description: string; // Can be symbol name or option name
  open: number;
  ask: number;
  bid: number;
  high: number;
  low: number;
  decimal: number;
  lastUpdated: number;
  status: ESymbolStatus;
  side: EOptionSymbolSide;
  strikePrice: number;
  expiration: string;
  miniChart: IMiniChart[];
  watch: boolean;
  // pin: number | null; // number is pin, null is unpin
  online: boolean;
  lastPrice: number | null;
  prevClose: number | null;
  tickRulesGroup: ISymbolTickRulesGroup[];
}

export interface ISymbolTickRulesGroup {
  startTickPriceRange: number;
  tickIncrement: number;
}

export interface IMiniChart {
  chartTime: number;
  open: number;
}

export interface ISymbolDetail extends ISymbol {
  currency: string;
  digits: number;
  stopsLevel: number;
  contractSize: number;
  marginCurrency: string;
  profitMode: EProfitMode;
  marginMode: EMarginMode;
  marginHedged: number;
  marginPercentage: number;
  marginInitial: number;
  marginMaintenance: number;
  tradeMode: ETradeMode;
  exeMode: EExecutionMode;
  gtcPendings: EGtcPendings;
  swapType: ESwapType;
  swapLong: number;
  swapShort: number;
  swapRollover3days: ESwapRollover3Days;
  minContractLotSize: number;
  maxContractLotSize: number;
  lotSizeStep: number;
  pointStep: number;
  marketEOD: number;
  tradingSessions: ITradingSession[];
  tradingEpochSessions: ITradingEpochSessions[];
  profile: string;
  industry: string;
  indexName?: string;
  revenue?: string;
  totalAssets?: string;
  operatingIncome?: string;
  netIncome?: string;
  totalEquity?: string;
  symbolFillPolicy?: ESymbolFillPolicy;
  tradeFillPolicy?: ITradeFillPolicy;
  maxQuoteDelay: number;
  exchangeRate?: number;
  quoteCurrencyExchangeRate?: number;
  tickSize: number;
  tickValue: number;
  enable: boolean;
  tradable: boolean;
  tradableExeMode: boolean;
  closable: boolean;
  holiday: boolean;
  /** To have more field for equity */
  tradableSecurityTypes: ESymbolCategory[];
  underlyingSymbols: string[];
  securityId: string;
  priceType: ESymbolPriceType;
  excerciseStyle: EOptionExcersiceStyle;
  /** Centroid */
  onePipEqual: number;
  spread?: number;
}

export interface ITradingSession {
  dayOfWeek: number;
  openDay: number;
  openTime: string;
  closeDay: number;
  closeTime: string;
}

export interface ITradingEpochSessions {
  dayOfWeek: number;
  tradingStartTime: number;
  tradingEndTime: number;
}

export interface ITradeFillPolicy {
  marketFillPolicy?: EFillPolicy[];
  limitFillPolicy?: EFillPolicy[];
  stopFillPolicy?: EFillPolicy[];
  stopLimitFillPolicy?: EFillPolicy[];
}
